import { Cell, Element } from '../../services/commission.service';

// Builds a seller's commission report from their scoped cells (their own Seller cells, plus base cells
// sharing a fee type with those). Two display modes per fee type:
//   - flagged unsummedOffset (e.g. Retail Markup): show base + seller lines separately, don't net them
//   - everything else (e.g. Commission): sum a seller cell with any base cell whose dimensions are a
//     subset of it — same stacking the pricing engine already does for unmatched dimensions
//
// Assumes `cells` are already scoped to one distributor — that filtering happens server-side.

export interface CommissionDimensionValue {
  dimensionTitle: string;
  elementTitle: string;
}

export interface CommissionBreakdownLine {
  feeTypeTitle: string;
  source: 'base' | 'seller';
  dimensionValues: CommissionDimensionValue[];
  value: number;
}

export interface CommissionRow {
  feeTypeTitle: string;
  dimensionValues: CommissionDimensionValue[];
  total: number;
  /** A formula row has no fixed total — it shows its rule, e.g. "100% of Premium, Admin". */
  rule?: string;
}

export interface DistributorCommissionReport {
  breakdown: CommissionBreakdownLine[];
  rows: CommissionRow[];
}

function normalizeTitle(value: string): string {
  return value.toLowerCase().replace(/[\s\-_/]+/g, '');
}

function isSystemDimension(element: Element, normalizedTitle: string): boolean {
  return !!element.dimension?.isSystem && normalizeTitle(element.dimension.title) === normalizedTitle;
}

function elementsOf(cell: Cell): Element[] {
  return (cell.elements ?? [])
    .map(cellElement => cellElement.element)
    .filter((element): element is Element => !!element);
}

function findElement(cell: Cell, matches: (element: Element) => boolean): Element | undefined {
  return elementsOf(cell).find(matches);
}

function elementLabel(element: Element): string {
  const segments: string[] = [];
  const visited = new Set<string>();
  let cursor: Element | null | undefined = element;

  while (cursor && !visited.has(cursor.id)) {
    visited.add(cursor.id);
    segments.unshift(cursor.title);
    cursor = cursor.parent ?? undefined;
  }

  return segments.length > 0 ? segments.join(' - ') : element.title;
}

function dimensionValuesFor(cell: Cell, excludeDimensionIds: Set<string>): CommissionDimensionValue[] {
  return elementsOf(cell)
    .filter(element => !excludeDimensionIds.has(element.dimensionId))
    .map(element => ({
      dimensionTitle: element.dimension?.title ?? element.dimensionId,
      elementTitle: elementLabel(element),
    }))
    .sort((left, right) => left.dimensionTitle.localeCompare(right.dimensionTitle));
}

function signatureOf(cell: Cell, excludeDimensionIds: Set<string>): string {
  return elementsOf(cell)
    .filter(element => !excludeDimensionIds.has(element.dimensionId))
    .map(element => `${element.dimensionId}:${element.id}`)
    .sort()
    .join('|');
}

function isSubsetSignature(subset: string, superset: string): boolean {
  if (subset === '') return true;
  if (subset === superset) return true;

  const supersetParts = new Set(superset.split('|'));
  return subset.split('|').every(part => supersetParts.has(part));
}

export function computeDistributorCommissionReport(cells: Cell[]): DistributorCommissionReport {
  const sellerElement = (cell: Cell) => findElement(cell, element => isSystemDimension(element, 'seller'));
  const feeTypeElement = (cell: Cell) => findElement(cell, element => isSystemDimension(element, 'feetype'));

  const excludeDimensionIds = new Set<string>();
  for (const cell of cells) {
    for (const element of elementsOf(cell)) {
      if (isSystemDimension(element, 'seller') || isSystemDimension(element, 'feetype')) {
        excludeDimensionIds.add(element.dimensionId);
      }
    }
  }

  const sellerCells = cells.filter(cell => !!sellerElement(cell));
  const baseCells = cells.filter(cell => !sellerElement(cell));

  const relevantFeeTypeIds = new Set(
    sellerCells.map(cell => feeTypeElement(cell)?.id).filter((id): id is string => !!id)
  );

  // Formula cells depend on each sale, so they can't be stacked with fixed amounts — each gets its own row.
  const formulaCells = sellerCells.filter(cell => !!cell.formula);

  const candidatePool = [
    ...sellerCells.filter(cell => !cell.formula),
    ...baseCells.filter(cell => {
      const feeTypeId = feeTypeElement(cell)?.id;
      return !!feeTypeId && relevantFeeTypeIds.has(feeTypeId);
    }),
  ];

  const groups = new Map<string, { feeTypeTitle: string; unsummedOffset: boolean; cells: Cell[] }>();
  for (const cell of candidatePool) {
    const feeType = feeTypeElement(cell);
    if (!feeType) continue;

    const group = groups.get(feeType.id) ?? {
      feeTypeTitle: feeType.title,
      unsummedOffset: !!feeType.unsummedOffset,
      cells: [],
    };
    group.cells.push(cell);
    groups.set(feeType.id, group);
  }

  const breakdown: CommissionBreakdownLine[] = [];
  const rows: CommissionRow[] = [];

  for (const { feeTypeTitle, unsummedOffset, cells: groupCells } of groups.values()) {
    if (unsummedOffset) {
      for (const cell of groupCells) {
        breakdown.push({
          feeTypeTitle,
          source: sellerElement(cell) ? 'seller' : 'base',
          dimensionValues: dimensionValuesFor(cell, excludeDimensionIds),
          value: Number(cell.value),
        });
      }
      continue;
    }

    // Two cells at the exact same combination shouldn't happen, but sum them together just in case.
    const bySignature = new Map<string, { cell: Cell; signature: string; value: number }>();
    for (const cell of groupCells) {
      const signature = signatureOf(cell, excludeDimensionIds);
      const existing = bySignature.get(signature);
      if (existing) existing.value += Number(cell.value);
      else bySignature.set(signature, { cell, signature, value: Number(cell.value) });
    }
    const candidates = [...bySignature.values()];

    const leaves = candidates.filter(
      candidate =>
        !candidates.some(
          other => other.signature !== candidate.signature && isSubsetSignature(candidate.signature, other.signature)
        )
    );

    for (const leaf of leaves) {
      const total = candidates
        .filter(candidate => isSubsetSignature(candidate.signature, leaf.signature))
        .reduce((sum, candidate) => sum + candidate.value, 0);

      rows.push({
        feeTypeTitle,
        dimensionValues: dimensionValuesFor(leaf.cell, excludeDimensionIds),
        total,
      });
    }
  }

  for (const cell of formulaCells) {
    const feeType = feeTypeElement(cell);
    if (!feeType || !cell.formula) continue;

    rows.push({
      feeTypeTitle: feeType.title,
      dimensionValues: dimensionValuesFor(cell, excludeDimensionIds),
      total: 0,
      rule: `${cell.formula.percent}% of ${cell.formula.feeTypeTitles.join(', ')}`,
    });
  }

  breakdown.sort(
    (left, right) => left.feeTypeTitle.localeCompare(right.feeTypeTitle) || left.source.localeCompare(right.source)
  );
  rows.sort((left, right) => left.feeTypeTitle.localeCompare(right.feeTypeTitle));

  return { breakdown, rows };
}

// A plan can have multiple unsummed or summed fee types at once, so group each bucket by fee type —
// otherwise the page would render one section mislabeled with just the first fee type's name.

export interface GroupedCommissionBreakdown {
  feeTypeTitle: string;
  lines: CommissionBreakdownLine[];
}

export interface GroupedCommissionRows {
  feeTypeTitle: string;
  rows: CommissionRow[];
}

export function groupBreakdownByFeeType(breakdown: CommissionBreakdownLine[]): GroupedCommissionBreakdown[] {
  const groups = new Map<string, CommissionBreakdownLine[]>();
  for (const line of breakdown) {
    const lines = groups.get(line.feeTypeTitle) ?? [];
    lines.push(line);
    groups.set(line.feeTypeTitle, lines);
  }

  return [...groups.entries()].map(([feeTypeTitle, lines]) => ({ feeTypeTitle, lines }));
}

export function groupRowsByFeeType(rows: CommissionRow[]): GroupedCommissionRows[] {
  const groups = new Map<string, CommissionRow[]>();
  for (const row of rows) {
    const rowsForType = groups.get(row.feeTypeTitle) ?? [];
    rowsForType.push(row);
    groups.set(row.feeTypeTitle, rowsForType);
  }

  return [...groups.entries()].map(([feeTypeTitle, rows]) => ({ feeTypeTitle, rows }));
}
